/**
 * Zod building blocks for the backend's serialisation quirks (rgs-api-contract skill):
 * BIGINT/DECIMAL as strings, UUIDs as hex or {type:'Buffer'}, JSON columns sometimes
 * returned as JSON strings.
 */
import { z, type ZodType } from 'zod';

const bufferShape = z.object({ type: z.literal('Buffer'), data: z.array(z.number().int().min(0).max(255)) });

/** 32-char hex UUID from either wire form. */
export const hexId = z.union([z.string(), bufferShape]).transform((v, ctx) => {
  const hex =
    typeof v === 'string'
      ? v.replace(/-/g, '').toLowerCase()
      : v.data.map((b) => b.toString(16).padStart(2, '0')).join('');
  if (!/^[0-9a-f]+$/.test(hex)) {
    ctx.addIssue({ code: 'custom', message: 'expected a hex id' });
    return z.NEVER;
  }
  return hex;
});

/** BIGINT ids and bitmasks: keep them as strings so nothing loses precision. */
export const bigintString = z.union([z.string(), z.number()]).transform((v, ctx) => {
  const s = String(v).trim();
  if (!/^-?\d+$/.test(s)) {
    ctx.addIssue({ code: 'custom', message: 'expected an integer' });
    return z.NEVER;
  }
  return s;
});

/** DECIMAL columns (rates, money as "12.34") as numbers for display. */
export const decimal = z.union([z.string(), z.number()]).transform((v, ctx) => {
  const n = typeof v === 'number' ? v : Number(v.trim());
  if (!Number.isFinite(n)) {
    ctx.addIssue({ code: 'custom', message: 'expected a decimal number' });
    return z.NEVER;
  }
  return n;
});

/** 0/1 tinyint flags. */
export const flag = z
  .union([z.boolean(), z.number(), z.string()])
  .transform((v) => v === true || v === 1 || v === '1' || v === 'true');

/** A JSON column that may arrive parsed or as a JSON string (e.g. GET /games/:id). */
export const jsonColumn = <T>(schema: ZodType<T>) =>
  z.preprocess((v) => {
    if (typeof v !== 'string') return v;
    try {
      return JSON.parse(v) as unknown;
    } catch {
      return v;
    }
  }, schema);

/**
 * DECIMAL columns kept exact, for values the UI edits and sends back (rates): validates and
 * trims trailing zeros, `"4.1779580000"` → `"4.177958"`, `"1.0000000000"` → `"1"`.
 */
export const decimalString = z.union([z.string(), z.number()]).transform((v, ctx) => {
  const s = String(v).trim();
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s);
  if (!match) {
    ctx.addIssue({ code: 'custom', message: 'expected a decimal number' });
    return z.NEVER;
  }
  const [, sign = '', whole = '', frac = ''] = match;
  const int = whole.replace(/^0+(?=\d)/, '');
  const fraction = frac.replace(/0+$/, '');
  const out = fraction ? `${int}.${fraction}` : int;
  return out === '0' ? '0' : `${sign}${out}`;
});
